import * as React from "react";

export type PromptStudioScope = "global" | "coach";

export interface ScopeCoach {
  id: number;
  label: string;
}

export interface PromptStudioScopeState {
  scope: PromptStudioScope;
  coach: ScopeCoach | null;
  coachId: number | null;
  setScope: (scope: PromptStudioScope) => void;
  setCoach: (coach: ScopeCoach | null) => void;
}

type Snapshot = { scope: PromptStudioScope; coach: ScopeCoach | null };

const INITIAL: Snapshot = { scope: "global", coach: null };
let snapshot: Snapshot = INITIAL;
const listeners = new Set<() => void>();

function update(next: Snapshot) {
  snapshot = next;
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only reset of the admin scope. */
export function resetPromptStudioScope(): void {
  update(INITIAL);
}

export const PromptStudioScopeContext = React.createContext<PromptStudioScopeState | undefined>(undefined);

/**
 * Admin scope (Global | Coach + coach picker). Held in memory so it survives moving between the
 * admin list, New Design, and editor, and resets to Global on reload; it is never put in the URL.
 */
export const PromptStudioScopeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { scope, coach } = React.useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => INITIAL
  );

  const value = React.useMemo<PromptStudioScopeState>(
    () => ({
      scope,
      coach,
      coachId: coach?.id ?? null,
      setScope: nextScope => update({ scope: nextScope, coach: nextScope === "coach" ? snapshot.coach : null }),
      setCoach: nextCoach => update({ scope: nextCoach ? "coach" : snapshot.scope, coach: nextCoach }),
    }),
    [scope, coach]
  );

  return <PromptStudioScopeContext.Provider value={value}>{children}</PromptStudioScopeContext.Provider>;
};

export function usePromptStudioScope(): PromptStudioScopeState {
  const context = React.useContext(PromptStudioScopeContext);
  if (!context) throw new Error("usePromptStudioScope must be used within PromptStudioScopeProvider");
  return context;
}
